import { useState, useCallback, useEffect } from 'react'
import UserService from './../../../../shared/services/User.service'
import UserProfileService from './../../../../shared/services/UserProfile.service'
import AgentProfileStore from './../../AgentProfileIntake/AgentProfile.store'
import { createHeadshotFormData } from '../utils/imageProcessing'

const getViewedUserIdFromUrl = () => {
  if (typeof window === 'undefined') return null
  const match = window.location.pathname.match(/^\/(\d+)\/account-profile/i)
  return match ? match[1] : null
}

/**
 * Custom hook for managing headshot upload functionality
 * @returns {Object} - Upload state and handlers
 */
export const useHeadshotUpload = () => {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadSuccess, setUploadSuccess] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [currentHeadshot, setCurrentHeadshot] = useState(UserProfileService.get('u_picture'))

  useEffect(() => {
    const viewedUserId = getViewedUserIdFromUrl()
    if (!viewedUserId) return

    const loggedInUserId = UserProfileService.getUserId()
    if (loggedInUserId && parseInt(viewedUserId, 10) === parseInt(loggedInUserId, 10)) return

    let cancelled = false
    UserService.get(viewedUserId)
      .then(viewedUser => {
        if (cancelled) return
        setCurrentHeadshot(viewedUser?.u_picture || null)
      })
      .catch(() => {
        if (!cancelled) setCurrentHeadshot(null)
      })

    return () => {
      cancelled = true
    }
  }, [])

  /**
   * Checks if the headshot is valid for the current user
   */
  const getIsValid = useCallback(() => {
    return UserProfileService.isA(['affiliate-group', 'associate-group']) || !!UserProfileService.get('u_picture')
  }, [])

  /**
   * Uploads the headshot to the server
   * @param {Blob} imageBlob - The image blob to upload
   */
  const uploadHeadshot = useCallback(
    async imageBlob => {
      if (!imageBlob || !(imageBlob instanceof Blob)) {
        setUploadError('Invalid image data')
        return false
      }

      setIsUploading(true)
      setUploadError(null)
      setUploadSuccess(false)

      try {
        const formData = createHeadshotFormData(imageBlob)
        const userId = UserProfileService.getUserId()

        // Upload the headshot
        await UserService.uploadHeadshot(userId, formData)

        // Fetch updated user data
        const updatedUser = await UserService.get(userId)

        // Update the user profile service and local state
        if (updatedUser?.u_picture) {
          UserProfileService.set('u_picture', updatedUser.u_picture)
          setCurrentHeadshot(updatedUser.u_picture)
          setUploadSuccess(true)

          // Update validity in the store
          AgentProfileStore.setValidity('headshot', getIsValid())

          return true
        } else {
          throw new Error('Upload succeeded but no picture returned')
        }
      } catch (err) {
        console.error('Failed to upload headshot:', err)
        setUploadError(err.message || 'Failed to upload headshot')
        return false
      } finally {
        setIsUploading(false)
      }
    },
    [getIsValid]
  )

  /**
   * Refreshes the current headshot, defaulting to the logged-in user's picture
   * @param {string} [picture] - u_picture to show
   */
  const refreshHeadshot = useCallback((picture = UserProfileService.get('u_picture')) => {
    setCurrentHeadshot(picture)
    return picture
  }, [])

  /**
   * Clears upload status messages
   */
  const clearUploadStatus = useCallback(() => {
    setUploadSuccess(false)
    setUploadError(null)
  }, [])

  return {
    // State
    isUploading,
    uploadSuccess,
    uploadError,
    currentHeadshot,

    // Handlers
    uploadHeadshot,
    refreshHeadshot,
    clearUploadStatus,
    getIsValid,
  }
}
